import i18n from "/common/plugins/vue-i18n.js";
import UpGPTPortalDataServices from "@/services/UpGPTPortalDataServices";

/**
 * Work Agent 模型清單 —— 前端這一側的唯一一份解讀邏輯。
 *
 * 為什麼要一個模組而不是寫在元件裡：清單從靜態陣列改成 API 之後多出五件事
 * （非同步取得、避免重複請求、決定預設選取、合併兩個非模型選項、失敗時要顯示
 * 什麼）。這五件事散在元件裡的話，UpAgent 的模型下拉來的時候會再長一份，
 * 然後兩份會慢慢不一致。
 *
 * 與後端 resolver 的關係：責任劃分相同（這件事的邏輯只放一個地方），但實作
 * 決定刻意不同 —— 後端 resolver 明文不快取，這裡快取。理由是邊界性質不同：
 * 後端在同一個 process 內、一次查詢成本可忽略；這裡跨 HTTP，而一個 Work Agent
 * 可能有十幾個欄位，每開一次欄位屬性對話框打一次 API 是白費。
 *
 * TODO: UpAgent 的模型下拉（FRD §4）落地時，把「取清單 + 預設值判定」這層抽成
 * 與 agent 類型無關的共用部分；保留值合併是 Work Agent 欄位語意，不該一起抽。
 */

// 兩個非模型選項：它們是欄位的輸入方式，不是模型，後端清單不會回傳，
// 一律由前端提供。識別碼與既有行為完全一致（userinput / python）。
export const MODEL_ID_USER_INPUT = "userinput";
export const MODEL_ID_PYTHON_TOOL = "python";

export const LOAD_ERROR_NOT_CONFIGURED = "NOT_CONFIGURED";
export const LOAD_ERROR_FETCH_FAILED = "FETCH_FAILED";

// module-level 快取：頁面重載即失效。不做長效快取 —— 管理員改了 DB 的清單，
// 使用者重載頁面就該看到新的。inflight 用來讓同時開多個對話框只打一次 API。
let cachedModels = null;
let cachedTenant = null;
let inflight = null;

// 快取綁在租戶上：登出後改登入另一個租戶時，清單必須跟著換。
//
// 為什麼不是在 logoutUser() 裡呼叫 clearCache()：那會讓 services/api/auth.js
// import 本模組，形成 auth -> workAgentModelService -> UpGPTPortalDataServices
// -> auth 的循環相依。綁租戶不需要任何一方認識另一方，而且連「登出流程沒走到
// logoutUser」的情況（例如 refresh token 失敗那條路）也一起涵蓋了。
//
// 直接讀 localStorage 的 'tenant' 而不是 import auth.js 的 TENANT 常數，
// 同樣是為了避免那個循環；那個 key 的名稱見 services/api/auth.js 的 TENANT。
function currentTenant() {
  try {
    return window.localStorage.getItem("tenant") || "";
  } catch (e) {
    return "";
  }
}

function reservedOptions() {
  return [
    { id: MODEL_ID_USER_INPUT, name: "User Input" },
    { id: MODEL_ID_PYTHON_TOOL, name: i18n.t("DOCUMENT_AGENT_PROPERTY.PYTHON_TOOL") },
  ];
}

/**
 * 取得該租戶的模型清單（六個欄位的原始形狀）。失敗時 throw 一個帶 code 的錯誤。
 */
async function fetchModels() {
  const tenant = currentTenant();
  if (tenant !== cachedTenant) clearCache();

  if (cachedModels) return cachedModels;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await UpGPTPortalDataServices.getDocumentAgentModelList();
      const list = Array.isArray(res?.data) ? res.data : [];
      cachedModels = list;
      cachedTenant = tenant;
      return list;
    } catch (err) {
      // 400 = 租戶尚未建立模型偏好設定，或內容不合法（後端一律回 400，不做
      // fallback）。這與「網路/伺服器出問題」要顯示不同的訊息 —— 前者要去補
      // 設定，後者是重試。
      const status = err?.response?.status;
      const e = new Error("WORK_AGENT_MODEL_LIST_LOAD_FAILED");
      e.code = status === 400 ? LOAD_ERROR_NOT_CONFIGURED : LOAD_ERROR_FETCH_FAILED;
      throw e;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/**
 * 給下拉用的完整選項清單：兩個保留選項 + 該租戶的 LLM 模型。
 *
 * 回傳 { options, error }，**不 throw** —— 呼叫端只要判斷 error 是否為 null。
 * 取清單失敗時 options 仍含兩個保留選項（使用者至少還能選 User Input），
 * 但不含任何 LLM 模型。
 *
 * 後端給的是 { key, display_name, ... }，v-select 綁的是 item-value='id' /
 * item-text='name'，所以欄位對映在這裡做完，元件樣板一行都不用改。
 */
export async function getOptions() {
  try {
    const models = await fetchModels();
    return {
      options: [
        ...reservedOptions(),
        ...models.map((m) => ({
          id: m.key,
          name: m.display_name,
          provider: m.provider,
          is_default: m.is_default === true,
          supports_vision: m.supports_vision === true,
        })),
      ],
      error: null,
    };
  } catch (err) {
    return { options: reservedOptions(), error: err.code || LOAD_ERROR_FETCH_FAILED };
  }
}

/**
 * 預設該選哪一個：is_default 為真的那筆；一筆都沒有時取清單第一筆模型
 * （決策 A3 的邊界規則，與後端 resolver.select_default 一致）。
 *
 * 沒有任何 LLM 模型可選時回傳空字串，讓必填驗證去擋，不要靜默塞保留值。
 */
export function pickDefaultModelId(options) {
  const models = (options || []).filter((o) => !isReservedModelId(o.id));
  if (!models.length) return "";
  const flagged = models.find((m) => m.is_default === true);
  return (flagged || models[0]).id;
}

export function isReservedModelId(id) {
  const value = String(id || "").trim().toLowerCase();
  return value === MODEL_ID_USER_INPUT || value === MODEL_ID_PYTHON_TOOL;
}

/**
 * 這個 id 在目前的選項清單裡嗎。
 *
 * 用於「欄位存的模型識別碼在清單裡找不到」的情形 —— 這不是理論情境：
 * seed_llm_model_preferences --check 顯示目前有 4 個租戶的既有欄位設定引用了
 * 不在清單內的 key（例如 gpt5-mini）。此時下拉不顯示選取值，由必填驗證迫使
 * 使用者重選，不得自動改成預設模型後靜默儲存。
 */
export function isKnownModel(options, id) {
  const value = String(id || "").trim();
  if (!value) return false;
  return (options || []).some((o) => String(o.id) === value);
}

/**
 * 丟掉快取。租戶變更時由 fetchModels() 自動呼叫；也可在需要強制重取時明確呼叫
 * （例如日後做出「重新整理模型清單」的動作）。
 */
export function clearCache() {
  cachedModels = null;
  cachedTenant = null;
  inflight = null;
}

/**
 * File 欄位的 vision 模型預設值：預設模型支援 vision 就選它，
 * 否則取第一個支援 vision 的模型；都沒有回空字串讓必填驗證去擋。
 */
export function pickDefaultVisionModelId(options) {
  const models = (options || []).filter((o) => !isReservedModelId(o.id) && o.supports_vision === true);
  if (!models.length) return "";
  const flagged = models.find((m) => m.is_default === true);
  return (flagged || models[0]).id;
}

export default {
  getOptions,
  pickDefaultModelId,
  pickDefaultVisionModelId,
  isReservedModelId,
  isKnownModel,
  clearCache,
  MODEL_ID_USER_INPUT,
  MODEL_ID_PYTHON_TOOL,
  LOAD_ERROR_NOT_CONFIGURED,
  LOAD_ERROR_FETCH_FAILED,
};
